import { useEffect, useState } from "react";
import { getApiBaseUrl } from "@apps/dashboard/lib/env";

/** Respuesta de `GET /health/oauth` (misma forma que `getOAuthConfigurationHints` en la API). */
export type OAuthServerHints = {
  betterAuthUrl: string;
  expectedGoogleRedirectPath: string;
  expectedAppleRedirectPath: string;
  google: {
    envCredentialsPresent: boolean;
    clientIdMatchesWebPattern: boolean;
  };
  apple: {
    envCredentialsPresent: boolean;
  };
  timestamp: number;
};

export type OAuthServerHintsStatus = "idle" | "loading" | "ok" | "error";

/**
 * Consulta la API para saber si esta instancia tiene credenciales OAuth cargadas.
 * Evita mostrar Google/Apple cuando Better Auth no registrará el proveedor (`PROVIDER_NOT_FOUND`).
 */
export function useOAuthServerHints(): {
  data: OAuthServerHints | null;
  status: OAuthServerHintsStatus;
} {
  const [data, setData] = useState<OAuthServerHints | null>(null);
  const [status, setStatus] = useState<OAuthServerHintsStatus>("idle");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    const url = `${getApiBaseUrl()}/health/oauth`;
    fetch(url, { credentials: "omit" })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json() as Promise<OAuthServerHints>;
      })
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setStatus("ok");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, status };
}

/** Si `viteFlag` es true: en carga no mostrar; si falla el health, mantener comportamiento anterior (optimista). */
export function shouldShowSocialProvider(
  viteFlag: boolean,
  hintsStatus: OAuthServerHintsStatus,
  hints: OAuthServerHints | null,
  kind: "google" | "apple",
): boolean {
  if (!viteFlag) return false;
  if (hintsStatus === "loading") return false;
  if (hintsStatus === "error") return true;
  return Boolean(hints?.[kind]?.envCredentialsPresent);
}
